// Quote-aware message display (#533): segment an inbound body into new content, quoted
// history (reply markers and ">" nesting) and content repeated verbatim from earlier
// messages of the same thread. Pure text segmentation, display-side only — the stored
// text and everything extraction sees stay byte-identical, which the lossless-concat
// invariant below guarantees (blocks joined in order reproduce the input exactly).
export interface DisplayBlock {
  kind: "new" | "quoted" | "repeated";
  /** Exact slice of the body; concatenating all blocks reproduces the body. */
  text: string;
}

// Reply-attribution lines that introduce quoted history. The German "Am … schrieb …:"
// and English "On … wrote:" single-line forms are attribution markers: ordinary prose
// can match them ("Am Montag schrieb Ihre Kollegin Folgendes:"), so they only count
// when a ">" run actually follows. The Outlook-style separator lines are unambiguous
// and quote the whole remainder of the message (full-copy style).
const ATTRIBUTION_PATTERNS = [
  /^\s*Am\s.+\sschrieb\s.+:\s*$/,
  /^\s*On\s.+\swrote:\s*$/,
];

const SEPARATOR_PATTERN =
  /^\s*-{2,}\s*(?:Urspr(?:ü|ue)ngliche Nachricht|Original(?:-|\s)?Message|Weitergeleitete Nachricht|Forwarded message)\s*-{2,}\s*$/i;

function isAttributionLine(line: string): boolean {
  return ATTRIBUTION_PATTERNS.some((pattern) => pattern.test(line));
}

function isQuoteLine(line: string): boolean {
  return /^\s{0,3}>/.test(line);
}

function isBlankLine(line: string): boolean {
  return line.trim() === "";
}

/** Nesting depth of a quoted line: the number of leading ">" markers. */
export function quoteLineLevel(line: string): number {
  const match = /^\s{0,3}((?:>\s?)+)/.exec(line);
  if (!match) return 0;
  return (match[1]!.match(/>/g) ?? []).length;
}

/** Whitespace-collapsed form used for the repeated-history comparison. */
function normalizeParagraph(paragraph: string): string {
  return paragraph.replace(/\s+/g, " ").trim();
}

const REPEATED_MIN_LENGTH = 40;

/** Normalized paragraphs of the earlier thread messages, keyed for exact lookup. */
export function earlierParagraphSet(earlierBodies: string[]): Set<string> {
  const set = new Set<string>();
  for (const body of earlierBodies) {
    for (const paragraph of (body ?? "").split(/\n\s*\n/)) {
      const normalized = normalizeParagraph(paragraph);
      if (normalized.length >= REPEATED_MIN_LENGTH) set.add(normalized);
    }
  }
  return set;
}

type LineKind = "new" | "quoted" | "repeated";

/**
 * Split `body` into display blocks. Quoted history is the contiguous run of ">" lines
 * (blank lines between quoted lines stay inside the run), an attribution marker with
 * the ">" run it introduces, and — for the unambiguous Outlook-style separator — the
 * whole remainder of the message. An attribution-looking line without a following ">"
 * run stays ordinary prose, and content that bottom-posts *below* a ">" run stays
 * visible. Paragraphs repeated verbatim (whitespace-normalized, at least 40
 * characters) from earlier messages of the thread collapse as "repeated" even without
 * quote markers.
 */
export function messageDisplayBlocks(body: string, earlierBodies: string[]): DisplayBlock[] {
  if (!body) return [];
  const lines = body.split("\n");
  const kinds: LineKind[] = new Array(lines.length).fill("new");

  let index = 0;
  while (index < lines.length) {
    const line = lines[index]!;
    if (SEPARATOR_PATTERN.test(line)) {
      // Full-copy style: the separator owns the rest of the message.
      for (let i = index; i < lines.length; i += 1) kinds[i] = "quoted";
      break;
    }
    if (isAttributionLine(line)) {
      let next = index + 1;
      while (next < lines.length && isBlankLine(lines[next]!)) next += 1;
      if (next < lines.length && isQuoteLine(lines[next]!)) {
        // Attribution followed by a ">" run: quote the marker, the gap and the run.
        for (let i = index; i < next; i += 1) kinds[i] = "quoted";
        index = next;
        while (index < lines.length && (isQuoteLine(lines[index]!) || (isBlankLine(lines[index]!) && index + 1 < lines.length && isQuoteLine(lines[index + 1]!)))) {
          kinds[index] = "quoted";
          index += 1;
        }
        continue;
      }
      // No ">" run follows: treat the line as ordinary prose, not a quote.
      index += 1;
      continue;
    }
    if (isQuoteLine(line)) {
      while (index < lines.length && (isQuoteLine(lines[index]!) || (isBlankLine(lines[index]!) && index + 1 < lines.length && isQuoteLine(lines[index + 1]!)))) {
        kinds[index] = "quoted";
        index += 1;
      }
      continue;
    }
    index += 1;
  }

  // Repeated-history pass over the lines that are still "new": compare each blank-line
  // separated paragraph against the earlier messages of the thread.
  const earlier = earlierParagraphSet(earlierBodies);
  if (earlier.size > 0) {
    let start = 0;
    while (start < lines.length) {
      if (kinds[start] !== "new" || isBlankLine(lines[start]!)) {
        start += 1;
        continue;
      }
      let end = start;
      while (end + 1 < lines.length && kinds[end + 1] === "new" && !isBlankLine(lines[end + 1]!)) end += 1;
      const paragraph = lines.slice(start, end + 1).join("\n");
      if (earlier.has(normalizeParagraph(paragraph))) {
        for (let i = start; i <= end; i += 1) kinds[i] = "repeated";
      }
      start = end + 1;
    }
    // Blank lines between two repeated stretches belong to the repeated region so the
    // collapse swallows the separators too.
    for (let i = 1; i < lines.length - 1; i += 1) {
      if (kinds[i] === "new" && isBlankLine(lines[i]!) && kinds[i - 1] === "repeated") {
        let j = i;
        while (j < lines.length && kinds[j] === "new" && isBlankLine(lines[j]!)) j += 1;
        if (j < lines.length && kinds[j] === "repeated") {
          for (let k = i; k < j; k += 1) kinds[k] = "repeated";
        }
      }
    }
  }

  // Assemble contiguous same-kind runs back into blocks, re-attaching the "\n"
  // separators so the concatenation of block texts is exactly the input body.
  const blocks: DisplayBlock[] = [];
  let runStart = 0;
  for (let i = 1; i <= lines.length; i += 1) {
    if (i === lines.length || kinds[i] !== kinds[runStart]) {
      const text = lines.slice(runStart, i).join("\n") + (i === lines.length ? "" : "\n");
      blocks.push({ kind: kinds[runStart]!, text });
      runStart = i;
    }
  }
  return blocks;
}
